import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useEffectiveTenantId } from "@hooks/useEffectiveTenantId";
import {
  getAdapterForProduct,
  lookupServiceType,
  productLabelsFromResourceCountByModule,
} from "./requests/productAdapters";
import {
  TAGS_MANAGER_ACCOUNT_API_MOUNT,
  getCategoriesList,
  type CategoryApiRow,
} from "./requests/request";
import {
  fetchTagsCatalog,
  getSnapshot,
  listAssignments,
  listUserAssignments,
  subscribe,
} from "./requests/tagsStore";
import { computeProductSpread, type TagRow } from "./TagsData";
import type {
  Tag,
  TagAssignment,
  TagsProductAdapter,
  UserAssignment,
} from "./types";

/** Stable empty snapshots for useSyncExternalStore when tagId is null or missing (must not allocate `[]` per render). */
const EMPTY_TAG_ASSIGNMENTS: TagAssignment[] = [];
const EMPTY_TAG_USER_ASSIGNMENTS: UserAssignment[] = [];

/**
 * Returns the active product's adapter (services + resource fetcher).
 * Falls back to Kontrol Edge when no product is selected yet, so the
 * picker UI is never empty.
 */
export function useActiveProductAdapter(): TagsProductAdapter {
  const selectedProduct = useAppSelector((s) => s.product.selectedProduct);
  return useMemo(
    () => getAdapterForProduct(selectedProduct),
    [selectedProduct]
  );
}

/**
 * Subscribes to the tags store. Renders update whenever tags or
 * assignments change anywhere in the app.
 *
 * The hook reads `state.selection.tenantId` from Redux and forwards it as
 * `?tenantId=` so Global Admins / Partners viewing a child tenant see the
 * tags of the lensed tenant — same convention every resource detail GET
 * uses (`fetchPort`, etc.). When the tenant lens changes, `refresh` is
 * recomputed and the store is re-fetched.
 */
export function useTags(options?: { enabled?: boolean }): {
  tags: Tag[];
  loading: boolean;
  error: string | null;
  hasFetched: boolean;
  refresh: () => Promise<void>;
} {
  const enabled = options?.enabled ?? true;

  // useSyncExternalStore is the React-recommended primitive for module
  // stores; it avoids tearing in concurrent rendering.
  useSyncExternalStore(subscribe, () => getSnapshot().tags, () => getSnapshot().tags);

  const api = useAxiosAuth0();
  // Tenant lens — `undefined` in partner / Global-Admin "all" view so the
  // backend resolves the scope from the JWT per the §3.0 hierarchy
  // (Global Admin → findAll; Partner → own + direct children); the child
  // tenant's id when the user has drilled into a specific customer.
  const tenantId = useEffectiveTenantId();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchTagsCatalog(
        api,
        TAGS_MANAGER_ACCOUNT_API_MOUNT,
        tenantId
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load tags");
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [api, tenantId]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  return {
    tags: getSnapshot().tags,
    loading: enabled ? loading : false,
    error: enabled ? error : null,
    hasFetched,
    refresh,
  };
}

/**
 * Subscribes to a single tag's assignments.
 */
export function useTagAssignments(tagId: string | null): {
  assignments: TagAssignment[];
  loading: boolean;
  refresh: () => Promise<void>;
} {
  useSyncExternalStore(
    subscribe,
    () =>
      tagId
        ? getSnapshot().assignments[tagId] ?? EMPTY_TAG_ASSIGNMENTS
        : EMPTY_TAG_ASSIGNMENTS,
    () =>
      tagId
        ? getSnapshot().assignments[tagId] ?? EMPTY_TAG_ASSIGNMENTS
        : EMPTY_TAG_ASSIGNMENTS
  );

  const [loading, setLoading] = useState(false);

  const api = useAxiosAuth0();
  const tenantId = useEffectiveTenantId();
  const refresh = useCallback(async () => {
    if (!tagId) return;
    setLoading(true);
    try {
      await listAssignments(
        api,
        TAGS_MANAGER_ACCOUNT_API_MOUNT,
        tagId,
        tenantId
      );
    } finally {
      setLoading(false);
    }
  }, [api, tagId, tenantId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    assignments: tagId
      ? getSnapshot().assignments[tagId] ?? EMPTY_TAG_ASSIGNMENTS
      : EMPTY_TAG_ASSIGNMENTS,
    loading,
    refresh,
  };
}

/**
 * Subscribes to a single tag's user assignments (the RBAC-style list of
 * users that can see resources tagged with this tag).
 */
export function useTagUserAssignments(tagId: string | null): {
  userAssignments: UserAssignment[];
  loading: boolean;
  refresh: () => Promise<void>;
} {
  useSyncExternalStore(
    subscribe,
    () =>
      tagId
        ? getSnapshot().userAssignments[tagId] ?? EMPTY_TAG_USER_ASSIGNMENTS
        : EMPTY_TAG_USER_ASSIGNMENTS,
    () =>
      tagId
        ? getSnapshot().userAssignments[tagId] ?? EMPTY_TAG_USER_ASSIGNMENTS
        : EMPTY_TAG_USER_ASSIGNMENTS
  );

  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!tagId) return;
    setLoading(true);
    try {
      await listUserAssignments(tagId);
    } finally {
      setLoading(false);
    }
  }, [tagId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    userAssignments: tagId
      ? getSnapshot().userAssignments[tagId] ?? EMPTY_TAG_USER_ASSIGNMENTS
      : EMPTY_TAG_USER_ASSIGNMENTS,
    loading,
    refresh,
  };
}

/**
 * Project the current tags + assignments map into `TagRow[]`. Used by
 * the list page (table + stats) and the global filter adapter so both
 * see the same derived shape (resourceCount + productSpread for
 * "Used in" / filters).
 *
 * Lives in hooks.ts (not TagsData.tsx) because the projection depends
 * on subscribing to the store; TagsData.tsx stays a pure data module.
 */
export function useTagRows(): {
  rows: TagRow[];
  loading: boolean;
  error: string | null;
  hasFetched: boolean;
  refresh: () => Promise<void>;
} {
  const { tags, loading, error, hasFetched, refresh } = useTags();
  const assignmentsByTag = useSyncExternalStore(
    subscribe,
    () => getSnapshot().assignments,
    () => getSnapshot().assignments
  );
  const resourceCountByTagId = useSyncExternalStore(
    subscribe,
    () => getSnapshot().resourceCountByTagId,
    () => getSnapshot().resourceCountByTagId
  );
  const rows = useMemo(
    () =>
      tags.map((t) => {
        const assignments = assignmentsByTag[t.id] ?? [];
        const apiCount = resourceCountByTagId[t.id];
        return {
          ...t,
          resourceCount:
            apiCount !== undefined ? apiCount : assignments.length,
          productSpread:
            t.resourceCountByModule !== undefined
              ? productLabelsFromResourceCountByModule(t.resourceCountByModule)
              : computeProductSpread(assignments),
        };
      }),
    [tags, assignmentsByTag, resourceCountByTagId]
  );
  return { rows, loading, error, hasFetched, refresh };
}

/**
 * Resolve a service-type key to its `{ service, product }` entry — works
 * cross-product so existing assignments render correctly even if the
 * active product can't see that service.
 */
export function useServiceLookup() {
  return useMemo(
    () => ({
      lookup: lookupServiceType,
    }),
    []
  );
}

/**
 * Fetches the full categories list from `GET …/tags/categories`.
 * Used by the tag form to populate the category picker with all existing
 * categories — not just those already attached to loaded tags.
 *
 * The list is small (tenant-scoped) so no pagination is applied.
 *
 * Fallback: when the API is unavailable (e.g. migration pending in staging),
 * derives categories from the tags already in the store so the form remains
 * usable with seed / cached data.
 */
export function useCategories(
  tenantIdOverride?: number,
  useOverride = false,
  scopeSelf = false
): {
  categories: CategoryApiRow[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const api = useAxiosAuth0();
  const lensTenantId = useEffectiveTenantId();
  // When `useOverride` is set, the caller (the tag form) is in control of the
  // tenant lens — even when the override resolves to `undefined` (the partner
  // case, where omitting `?tenantId=` is the correct wire value). Falling back
  // to the lens here would load the wrong tenant's categories when the form is
  // pinned to the partner while the global dropdown points at a child.
  const tenantId = useOverride ? tenantIdOverride : lensTenantId;
  const [categories, setCategories] = useState<CategoryApiRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCategoriesList(
        api,
        TAGS_MANAGER_ACCOUNT_API_MOUNT,
        tenantId,
        scopeSelf ? "self" : undefined
      );
      if (!res.data.success) {
        throw new Error(res.data.message || "Failed to load categories");
      }
      setCategories(res.data.data.items);
    } catch (e) {
      // API unavailable — derive from the tags already in the store so the
      // category picker chips remain visible (seed or previously cached data).
      const seen = new Map<number, CategoryApiRow>();
      for (const t of getSnapshot().tags) {
        if (t.category && !seen.has(t.category.id)) {
          seen.set(t.category.id, t.category);
        }
      }
      setCategories(
        Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
      );
      setError(e instanceof Error ? e.message : "Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, scopeSelf]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { categories, loading, error, refresh };
}
